"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MaterialEngine = void 0;
/* eslint-disable no-prototype-builtins */
const viewer_shared_node_tree_1 = require("@shapediver/viewer.shared.node-tree");
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
const viewer_shared_types_1 = require("@shapediver/viewer.shared.types");
const gl_matrix_1 = require("gl-matrix");
const materialDatabase_1 = require("./materialDatabase");
class MaterialEngine {
    constructor() {
        // #region Properties (4)
        this._converter = viewer_shared_services_1.Converter.instance;
        this._httpClient = viewer_shared_services_1.HttpClient.instance;
        this._logger = viewer_shared_services_1.Logger.instance;
        // #endregion Private Methods (3)
    }
    // #endregion Properties (4)
    // #region Public Static Accessors (1)
    static get instance() {
        return this._instance || (this._instance = new this());
    }
    // #endregion Public Static Accessors (1)
    // #region Public Methods (9)
    /**
     * Create a material data based on the material properties
     *
     * @param materialProperties
     * @returns
     */
    createMaterialData(materialProperties) {
        const materialType = materialProperties.type || viewer_shared_types_1.MATERIAL_TYPE.STANDARD;
        switch (materialType) {
            case viewer_shared_types_1.MATERIAL_TYPE.SPECULAR_GLOSSINESS:
                return new viewer_shared_types_1.MaterialSpecularGlossinessData(materialProperties);
            case viewer_shared_types_1.MATERIAL_TYPE.UNLIT:
                return new viewer_shared_types_1.MaterialUnlitData(materialProperties);
            case viewer_shared_types_1.MATERIAL_TYPE.GEM:
                return new viewer_shared_types_1.MaterialGemData(materialProperties);
            default:
                return new viewer_shared_types_1.MaterialStandardData(materialProperties);
        }
    }
    /**
       * Load the material content into a scene graph node.
       *
       * @param content the material content
       * @returns the scene graph node
       */
    loadContent(content) {
        return __awaiter(this, void 0, void 0, function* () {
            const node = new viewer_shared_node_tree_1.TreeNode(content.name || 'material');
            if (!content)
                throw new viewer_shared_services_1.ShapeDiverViewerDataProcessingError('MaterialEngine.loadContent: Invalid content was provided to material engine.');
            let material = new viewer_shared_types_1.MaterialStandardData();
            if (content.data) {
                const data = content.data;
                let presetData;
                if (data.materialpreset)
                    presetData = this.loadPresetMaterialDefinition(data.materialpreset);
                if (data.materialType && data.materialType !== 'standard') {
                    // gem material https://shapediver.atlassian.net/browse/SS-2514
                }
                else {
                    if (data.version) {
                        if (data.version === '1.0') {
                            material = yield this.loadMaterialV3(this.loadMaterialDefinitionV1(data, presetData));
                        }
                        else if (data.version === '2.0') {
                            material = yield this.loadMaterialV3(this.loadMaterialDefinitionV2(data, presetData));
                        }
                        else if (data.version === '3.0') {
                            material = yield this.loadMaterialV3(this.loadMaterialDefinitionV3(data, presetData));
                        }
                        else {
                            throw new viewer_shared_services_1.ShapeDiverViewerDataProcessingError('MaterialEngine.loadContent: Material data version not supported.');
                        }
                    }
                }
            }
            else {
                throw new viewer_shared_services_1.ShapeDiverViewerDataProcessingError('MaterialEngine.loadContent: No material data was provided to material engine.');
            }
            node.data.push(material);
            return node;
        });
    }
    loadMap(url, id) {
        return __awaiter(this, void 0, void 0, function* () {
            let response;
            if (!id) {
                response = yield this._httpClient.loadTexture(url);
            }
            else {
                response = yield this._httpClient.loadTexture('https://viewer.shapediver.com/v2/materials/1024/' + id + '/' + url);
            }
            if (typeof window !== 'undefined') {
                const image = yield viewer_shared_services_1.Converter.instance.responseToImage(response);
                return new viewer_shared_types_1.MapData(image, { blob: response.data.blob });
            }
            else {
                return new viewer_shared_types_1.MapData(response.data.buffer, { blob: response.data.blob });
            }
        });
    }
    loadMapWithProperties(texture) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this._httpClient.loadTexture(texture.href);
            const wrapS = texture.wrapS === 1 ? viewer_shared_types_1.TEXTURE_WRAPPING.CLAMP_TO_EDGE : texture.wrapS === 2 ? viewer_shared_types_1.TEXTURE_WRAPPING.MIRRORED_REPEAT : viewer_shared_types_1.TEXTURE_WRAPPING.REPEAT;
            const wrapT = texture.wrapT === 1 ? viewer_shared_types_1.TEXTURE_WRAPPING.CLAMP_TO_EDGE : texture.wrapT === 2 ? viewer_shared_types_1.TEXTURE_WRAPPING.MIRRORED_REPEAT : viewer_shared_types_1.TEXTURE_WRAPPING.REPEAT;
            const center = texture.center ? gl_matrix_1.vec2.fromValues(texture.center[0], texture.center[1]) : gl_matrix_1.vec2.fromValues(0, 0);
            const color = texture.color ? gl_matrix_1.vec4.fromValues(texture.color[0] / 255, texture.color[1] / 255, texture.color[2] / 255, texture.color[3] / 255) : gl_matrix_1.vec4.fromValues(1, 1, 1, 1);
            const offset = texture.offset ? gl_matrix_1.vec2.fromValues(texture.offset[0], texture.offset[1]) : gl_matrix_1.vec2.fromValues(0, 0);
            const repeat = texture.repeat ? gl_matrix_1.vec2.fromValues(texture.repeat[0], texture.repeat[1]) : gl_matrix_1.vec2.fromValues(1, 1);
            if (typeof window !== 'undefined') {
                const image = yield viewer_shared_services_1.Converter.instance.responseToImage(response);
                return new viewer_shared_types_1.MapData(image, { blob: response.data.blob, wrapS, wrapT, minFilter: viewer_shared_types_1.TEXTURE_FILTERING.LINEAR_MIPMAP_LINEAR, magFilter: viewer_shared_types_1.TEXTURE_FILTERING.LINEAR, center, color, offset, repeat, rotation: texture.rotation || 0 });
            }
            else {
                return new viewer_shared_types_1.MapData(response.data.buffer, { blob: response.data.blob, wrapS, wrapT, minFilter: viewer_shared_types_1.TEXTURE_FILTERING.LINEAR_MIPMAP_LINEAR, magFilter: viewer_shared_types_1.TEXTURE_FILTERING.LINEAR, center, color, offset, repeat, rotation: texture.rotation || 0 });
            }
        });
    }
    loadMaterialDefinitionV1(data, presetData = {}) {
        // ambient is ignored
        if (data.color) {
            presetData.color = this.multiplyColors(data.color, presetData.color);
        }
        else if (data.diffuse) {
            // multiply color with diffuse
            presetData.color = this.multiplyColors(data.diffuse, presetData.color);
        }
        // emission is ignored
        // specular is ignored
        if (data.shine || data.shine === 0) {
            presetData.metalness = Math.min(1, data.shine);
            presetData.roughness = 1 - (Math.min(1, data.shine));
        }
        if (data.hasOwnProperty('transparency'))
            presetData.transparency = data.transparency;
        if (data.bitmaptexture)
            presetData.bitmaptexture = {
                href: data.bitmaptexture
            };
        if (data.bumptexture)
            presetData.bumptexture = {
                href: data.bumptexture
            };
        if (data.transparencytexture)
            presetData.transparencytexture = {
                href: data.transparencytexture
            };
        return presetData;
    }
    loadMaterialDefinitionV2(data, presetData = {}) {
        // ambient is ignored
        if (data.color)
            presetData.color = this.multiplyColors(data.color, presetData.color);
        presetData.side = data.side;
        if (data.metalness || data.metalness === 0)
            presetData.metalness = data.metalness;
        if (data.roughness || data.roughness === 0)
            presetData.roughness = data.roughness;
        if (data.hasOwnProperty('transparency'))
            presetData.transparency = data.transparency;
        if (data.alphaThreshold || data.alphaThreshold === 0)
            presetData.alphaThreshold = data.alphaThreshold;
        if (data.bitmaptexture)
            presetData.bitmaptexture = {
                href: data.bitmaptexture
            };
        if (data.metalnesstexture)
            presetData.metalnesstexture = {
                href: data.metalnesstexture
            };
        if (data.roughnesstexture)
            presetData.roughnesstexture = {
                href: data.roughnesstexture
            };
        if (data.bumptexture)
            presetData.bumptexture = {
                href: data.bumptexture
            };
        if (data.normaltexture)
            presetData.normaltexture = {
                href: data.normaltexture
            };
        if (data.transparencytexture)
            presetData.transparencytexture = {
                href: data.transparencytexture
            };
        return presetData;
    }
    loadMaterialDefinitionV3(data, presetData = {}) {
        // ambient is ignored
        if (data.color)
            presetData.color = this.multiplyColors(data.color, presetData.color);
        presetData.side = data.side;
        if (data.metalness || data.metalness === 0)
            presetData.metalness = data.metalness;
        if (data.roughness || data.roughness === 0)
            presetData.roughness = data.roughness;
        if (data.hasOwnProperty('transparency'))
            presetData.transparency = data.transparency;
        if (data.alphaThreshold || data.alphaThreshold === 0)
            presetData.alphaThreshold = data.alphaThreshold;
        if (data.bumpAmplitude || data.bumpAmplitude === 0)
            presetData.bumpAmplitude = data.bumpAmplitude;
        if (data.bitmaptexture)
            presetData.bitmaptexture = data.bitmaptexture;
        if (data.metalnesstexture)
            presetData.metalnesstexture = data.metalnesstexture;
        if (data.roughnesstexture)
            presetData.roughnesstexture = data.roughnesstexture;
        if (data.bumptexture)
            presetData.bumptexture = data.bumptexture;
        if (data.normaltexture)
            presetData.normaltexture = data.normaltexture;
        if (data.transparencytexture)
            presetData.transparencytexture = data.transparencytexture;
        // line material https://shapediver.atlassian.net/browse/SS-2272
        return presetData;
    }
    loadMaterialV3(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const material = new viewer_shared_types_1.MaterialStandardData();
            const promises = [];
            // ambient is ignored
            if (data.color)
                material.color = data.color;
            material.side = data.side === 'front' ? viewer_shared_types_1.MATERIAL_SIDE.FRONT : data.side === 'back' ? viewer_shared_types_1.MATERIAL_SIDE.BACK : viewer_shared_types_1.MATERIAL_SIDE.DOUBLE;
            if (data.metalness || data.metalness === 0)
                material.metalness = data.metalness;
            if (data.roughness || data.roughness === 0)
                material.roughness = data.roughness;
            if (data.hasOwnProperty('transparency'))
                material.opacity = 1 - data.transparency;
            if (data.alphaThreshold || data.alphaThreshold === 0)
                material.alphaCutoff = data.alphaThreshold;
            if (data.bumpAmplitude || data.bumpAmplitude === 0)
                material.bumpScale = data.bumpAmplitude;
            if (data.bitmaptexture) {
                promises.push(this.loadMapWithProperties(data.bitmaptexture).then(map => {
                    if (map)
                        material.map = map;
                    return map;
                }));
            }
            if (data.metalnesstexture) {
                promises.push(this.loadMapWithProperties(data.metalnesstexture).then(map => {
                    if (map)
                        material.metalnessMap = map;
                    return map;
                }));
            }
            if (data.roughnesstexture) {
                promises.push(this.loadMapWithProperties(data.roughnesstexture).then(map => {
                    if (map)
                        material.roughnessMap = map;
                    return map;
                }));
            }
            if (data.bumptexture) {
                promises.push(this.loadMapWithProperties(data.bumptexture).then(map => {
                    if (map)
                        material.bumpMap = map;
                    return map;
                }));
            }
            if (data.normaltexture) {
                promises.push(this.loadMapWithProperties(data.normaltexture).then(map => {
                    if (map)
                        material.normalMap = map;
                    return map;
                }));
            }
            if (data.transparencytexture) {
                promises.push(this.loadMapWithProperties(data.transparencytexture).then(map => {
                    if (map)
                        material.alphaMap = map;
                    return map;
                }));
            }
            // line material https://shapediver.atlassian.net/browse/SS-2272
            yield Promise.all(promises);
            return material;
        });
    }
    loadPresetMaterial(preset) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.loadMaterialV3(this.loadPresetMaterialDefinition(preset));
        });
    }
    loadPresetMaterialDefinition(preset) {
        const definition = {};
        const idStrings = this.getClassAndSpecificId(preset);
        if (materialDatabase_1.materialDatabase[idStrings.class] && materialDatabase_1.materialDatabase[idStrings.class][idStrings.specific]) {
            this.assignSpecificDefinition(idStrings, materialDatabase_1.materialDatabase[idStrings.class][idStrings.specific], definition);
            this.assignGeneralDefinition(idStrings, materialDatabase_1.materialDatabase[idStrings.class].properties, materialDatabase_1.materialDatabase[idStrings.class][idStrings.specific], definition);
        }
        else if (materialDatabase_1.materialDatabase[idStrings.class] && materialDatabase_1.materialDatabase[idStrings.class]['00']) {
            this.assignSpecificDefinition({ class: idStrings.class, specific: '00' }, materialDatabase_1.materialDatabase[idStrings.class]['00'], definition);
            this.assignGeneralDefinition({ class: idStrings.class, specific: '00' }, materialDatabase_1.materialDatabase[idStrings.class].properties, materialDatabase_1.materialDatabase[idStrings.class]['00'], definition);
        }
        else {
            this.assignSpecificDefinition({ class: '00', specific: '00' }, materialDatabase_1.materialDatabase['00']['00'], definition);
            this.assignGeneralDefinition({ class: '00', specific: '00' }, materialDatabase_1.materialDatabase['00'].properties, materialDatabase_1.materialDatabase['00']['00'], definition);
        }
        return definition;
    }
    // #endregion Public Methods (9)
    // #region Private Methods (3)
    assignGeneralDefinition(id, generalDefinition, specificDefinition, definition) {
        if (generalDefinition.transparencytexture && !specificDefinition.transparencytexture)
            definition.transparencytexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + generalDefinition.transparencytexture
            };
        if (generalDefinition.hasOwnProperty('alphaThreshold') && !specificDefinition.hasOwnProperty('alphaThreshold'))
            definition.alphaThreshold = generalDefinition.alphaThreshold;
        if (generalDefinition.bumptexture && !specificDefinition.bumptexture)
            definition.bumptexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + generalDefinition.bumptexture
            };
        if (generalDefinition.hasOwnProperty('bumpAmplitude') && !specificDefinition.hasOwnProperty('bumpAmplitude'))
            definition.bumpAmplitude = generalDefinition.bumpAmplitude;
        if (generalDefinition.color && !specificDefinition.color)
            definition.color = generalDefinition.color;
        if (generalDefinition.bitmaptexture && !specificDefinition.bitmaptexture)
            definition.bitmaptexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + generalDefinition.bitmaptexture
            };
        if (generalDefinition.hasOwnProperty('metalness') && !specificDefinition.hasOwnProperty('metalness'))
            definition.metalness = generalDefinition.metalness;
        if (generalDefinition.metalnesstexture && !specificDefinition.metalnesstexture)
            definition.metalnesstexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + generalDefinition.metalnesstexture
            };
        if (generalDefinition.normaltexture && !specificDefinition.normaltexture)
            definition.normaltexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + generalDefinition.normaltexture
            };
        if (generalDefinition.hasOwnProperty('transparency') && !specificDefinition.hasOwnProperty('transparency'))
            definition.transparency = generalDefinition.transparency;
        if (generalDefinition.hasOwnProperty('roughness') && !specificDefinition.hasOwnProperty('roughness'))
            definition.roughness = generalDefinition.roughness;
        if (generalDefinition.roughnesstexture && !specificDefinition.roughnesstexture)
            definition.roughnesstexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + generalDefinition.roughnesstexture
            };
        if (generalDefinition.side && !specificDefinition.side)
            definition.side = generalDefinition.side;
    }
    assignSpecificDefinition(id, specificDefinition, definition) {
        if (specificDefinition.transparencytexture)
            definition.transparencytexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + id.specific + '/' + specificDefinition.transparencytexture
            };
        if (specificDefinition.hasOwnProperty('alphaThreshold'))
            definition.alphaThreshold = specificDefinition.alphaThreshold;
        if (specificDefinition.bumptexture)
            definition.bumptexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + id.specific + '/' + specificDefinition.bumptexture
            };
        if (specificDefinition.hasOwnProperty('bumpAmplitude'))
            definition.bumpAmplitude = specificDefinition.bumpAmplitude;
        if (specificDefinition.color)
            definition.color = specificDefinition.color;
        if (specificDefinition.bitmaptexture)
            definition.bitmaptexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + id.specific + '/' + specificDefinition.bitmaptexture
            };
        if (specificDefinition.hasOwnProperty('metalness'))
            definition.metalness = specificDefinition.metalness;
        if (specificDefinition.metalnesstexture)
            definition.metalnesstexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + id.specific + '/' + specificDefinition.metalnesstexture
            };
        if (specificDefinition.normaltexture)
            definition.normaltexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + id.specific + '/' + specificDefinition.normaltexture
            };
        if (specificDefinition.hasOwnProperty('transparency'))
            definition.transparency = specificDefinition.transparency;
        if (specificDefinition.hasOwnProperty('roughness'))
            definition.roughness = specificDefinition.roughness;
        if (specificDefinition.roughnesstexture)
            definition.roughnesstexture = {
                href: 'https://viewer.shapediver.com/v2/materials/1024/' + id.class + '/' + id.specific + '/' + specificDefinition.roughnesstexture
            };
        if (specificDefinition.side)
            definition.side = specificDefinition.side;
    }
    getClassAndSpecificId(id) {
        // for a while, we had documented the presets to be 10, 20, 30 and 40 here, we allow for the few cases where this was used to succeed
        if (id < 100 && id % 10 == 0)
            id /= 10;
        // if the id is less than 10, multiply it by 100
        if (id < 10)
            id *= 100;
        const cast = (id) => {
            const idString = String(id);
            return idString.padStart(2, '0').slice(0, 2);
        };
        return {
            class: cast(Math.floor(id / 100)),
            specific: cast(id - (Math.floor(id / 100) * 100))
        };
    }
    /**
     * Multiply two colors
     *
     * @param color1
     * @param color2
     * @returns
     */
    multiplyColors(color1, color2) {
        if (!color2)
            return color1;
        return [
            Math.min(255, (color1[0] * color2[0]) / 255),
            Math.min(255, (color1[1] * color2[1]) / 255),
            Math.min(255, (color1[2] * color2[2]) / 255),
            Math.min(255, ((color1[3] !== undefined ? color1[3] : 255) * (color2[3] !== undefined ? color2[3] : 255)) / 255)
        ];
    }
}
exports.MaterialEngine = MaterialEngine;
//# sourceMappingURL=MaterialEngine.js.map